import { JupiterTheme } from '@jupiterone/jupiter-web-toolkit';
import aceJSS from '@jupiterone/jupiter-web-toolkit/src/styles/AceEditor.styles';
import { Paper } from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import { createStyles, makeStyles } from '@material-ui/styles';
import React from 'react';
import { j1qlAutocomplete } from '@jupiterone/jupiter-web-toolkit';
import SearchIcon from 'react-feather/dist/icons/search';
import SearchBarHints from '../SearchBarHints/SearchBarHints';
import SearchPrompt from '../SearchPrompt/SearchPrompt';
import useSearchAceEditorLogic, { HookProps } from './useSearchAceEditorLogic';

const aceStyles = {
  fontSize: '16px',
  fontFamily: `'Ubuntu Mono', monospace`,
  lineHeight: 1.2,
  width: '100%',
};

const useStyles = makeStyles((theme: JupiterTheme) =>
  createStyles({
    root: {
      display: 'flex',
      boxShadow: theme.spacing(0, 0),
      borderRadius: 0,
      position: 'relative',
    },
    searchRoot: {
      flexGrow: 1,
    },
    aceContainer: {
      background: theme.palette.common.white,
      position: 'relative',
      display: 'flex',
      minHeight: 60,
      alignItems: 'center',
      flexGrow: 1,
      width: '100%',
      padding: theme.spacing(1, 2),
      '& .ace_editor': {
        background: `transparent none`,
        margin: `0 auto`,
        position: 'relative',
        zIndex: 1,
        color: theme.palette.text.primary,
        ...aceJSS(theme),
      },
      '& .ace_scroller.ace_scroll-left': {
        boxShadow: 'none',
      },
      '& .ace_scrollbar.ace_scrollbar-h': {
        display: 'none',
      },
    },
    searchIcon: {
      height: 29,
      width: 29,
    },
    searchButton: {
      alignSelf: 'flex-start',
      borderRadius: 0,
      height: 60,
      width: 60,
      color: theme.palette.coolGrey[300],
      position: 'relative',
      '&:after': {
        background: theme.palette.grey[300],
        content: `''`,
        display: 'block',
        width: 1,
        height: 60,
        position: 'absolute',
        right: -2,
        top: -1,
      },
      '&:hover svg': {
        color: theme.palette.primary.dark,
      },
    },
  })
);

interface Props extends HookProps {
  value: string;
}

const isFocusable = (
  element: HTMLElement | Element
): element is HTMLElement => {
  return (element as HTMLElement).focus !== undefined;
};

const SearchAceEditor = ({
  value,
  hasFocus,
  onChange,
  onSearch,
  onBlur,
  onFocus,
  autocomplete,
}: Props) => {
  const classes = useStyles();
  const {
    editorRef,
    aceCommands,
    collapseText,
    handleChange,
    handleFocus,
    handleBlur,
  } = useSearchAceEditorLogic({
    autocomplete,
    hasFocus,
    onChange,
    onSearch,
    onBlur,
    onFocus,
  });

  const focusForTyping = () => {
    handleFocus();
    const target = document.getElementsByClassName('ace_text-input');

    if (target !== null && target[0] !== null && isFocusable(target[0])) {
      target[0].focus();
    }
  };

  return (
    <Paper className={classes.root}>
      <div onClick={focusForTyping} className={classes.searchRoot}>
        <div className={classes.aceContainer}>
          <j1qlAutocomplete.aceCompleter.AceEditor
            ref={editorRef}
            mode="sql"
            width="auto"
            height="100%"
            theme="textmate"
            onChange={handleChange}
            name="editor:query"
            editorProps={{
              $blockScrolling: Infinity,
              id: 'ace-target-for-focus',
            }}
            value={value}
            showGutter={false}
            showPrintMargin={false}
            highlightActiveLine={false}
            focus
            minLines={1}
            maxLines={Infinity}
            wrapEnabled
            onFocus={handleFocus}
            onBlur={handleBlur}
            setOptions={{
              enableBasicAutocompletion: autocomplete
                ? ([j1qlAutocomplete.aceCompleter.aceCompleter] as any)
                : autocomplete,
              enableLiveAutocompletion: autocomplete
                ? ([j1qlAutocomplete.aceCompleter.aceCompleter] as any)
                : autocomplete,
            }}
            tabSize={0}
            commands={aceCommands as any}
            style={{
              ...aceStyles,
              maxHeight: collapseText ? 40 : undefined,
            }}
          />
          {!value && <SearchPrompt />}
        </div>
      </div>
      <SearchBarHints
        searchText={value}
        classes={{ iconButton: classes.searchButton }}
      />
      <Tooltip title="Search" enterDelay={500} placement="top">
        <IconButton
          onClick={onSearch}
          className={classes.searchButton}
          aria-label="Search"
        >
          <SearchIcon className={classes.searchIcon} />
        </IconButton>
      </Tooltip>
    </Paper>
  );
};

export default SearchAceEditor;
